import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiPost } from '../lib/api'
import { useAuthStore } from '../stores/authStore'

interface Store {
    legacyStoreId: string;
    name: string;
    city: string;
    province: string;
    isActive: boolean;
}

interface SyncSource {
    name: string; description: string; lastSyncUtc: string | null; lastSyncRelative: string
    recordCount: number; status: string
}
interface SyncStatus { sources: SyncSource[] }

const ROLE_RANK: Record<string, number> = {
    Unassigned: 0, Staff: 1, StoreManager: 2,
    StoreOwner: 3, HeadOffice: 4, SuperAdmin: 5
}
function SyncStatusPanel({ token }: { token: string | null }) {
    const qc = useQueryClient()
    const [syncing, setSyncing] = useState(false)

    const { data: syncStatus } = useQuery<SyncStatus>({
        queryKey: ['sync-status'],
        queryFn: () => api('/api/dashboard/sync-status', token),
        refetchInterval: 60_000,
        enabled: !!token
    })

    const handleSync = async () => {
        setSyncing(true)
        try {
            await apiPost('/api/dashboard/sync-now', token)
            qc.invalidateQueries({ queryKey: ['sync-status'] })
            qc.invalidateQueries({ queryKey: ['admin-stores'] }) // Also refresh the stores list
        } catch { /* ignore */ }
        finally { setSyncing(false) }
    }

    if (!syncStatus?.sources?.length) return (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>
            Loading sync status...
        </div>
    )

    return (
        <div className="sync-status-panel" style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-4)',
            padding: 'var(--space-4) var(--space-4)',
            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-6)',
            fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)',
            flexWrap: 'wrap'
        }}>
            <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem', color: 'var(--color-text)' }}>Legacy Database Sync Status</h3>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    {syncStatus.sources.map((src, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                                background: src.status === 'ok' ? '#10b981' : '#f59e0b'
                            }} />
                            <strong style={{ color: 'var(--color-text)' }}>{src.name}</strong>
                            <span>— {src.recordCount} records</span>
                            <span style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)' }}>
                                ({src.lastSyncRelative})
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <button
                className="btn btn-primary"
                onClick={handleSync}
                disabled={syncing}
                style={{ opacity: syncing ? 0.7 : 1, padding: '0.75rem 1.5rem' }}
            >
                {syncing ? '⟳ Syncing from Legacy...' : '⟳ Sync Now'}
            </button>
        </div>
    )
}


export function StoreAdminPage() {
    const { accessToken, user } = useAuthStore()
    const qc = useQueryClient()

    const userRank = ROLE_RANK[user?.role ?? 'Unassigned'] ?? 0

    const { data: stores, isLoading } = useQuery<Store[]>({
        queryKey: ['admin-stores'],
        queryFn: () => api('/api/admin/stores', accessToken),
        enabled: !!accessToken
    })

    const toggleStoreMutation = useMutation({
        mutationFn: ({ id, isActive }: { id: string, isActive: boolean }) =>
            apiPost(`/api/admin/stores/${id}/toggle`, accessToken, { isActive }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin-stores'] })
            qc.invalidateQueries({ queryKey: ['active-stores'] })
        }
    })

    if (userRank < 4) { // Requires HeadOffice level (rank 4) or SuperAdmin (rank 5)
        return <div style={{ padding: '2rem' }}>You do not have permission to view this page.</div>
    }

    return (
        <div style={{ padding: '2rem', maxWidth: 1000, margin: '0 auto' }}>
            <h1 style={{ marginBottom: '0.5rem' }}>Store Administration</h1>
            <p style={{ color: 'var(--color-text-tertiary)', marginBottom: '2rem' }}>
                Manage physical store locations, sync data from legacy MSSQL, and enable/disable stores for staff access.
            </p>

            <SyncStatusPanel token={accessToken} />

            <div style={{ background: 'var(--color-surface)', borderRadius: 12, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ background: 'rgba(255,255,255,0.02)' }}>
                        <tr>
                            <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)' }}>Store Key</th>
                            <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)' }}>Name</th>
                            <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)' }}>Location</th>
                            <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', textAlign: 'center' }}>Enabled</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr><td colSpan={4} style={{ padding: '2rem', textAlign: 'center' }}>Loading stores...</td></tr>
                        ) : stores?.length === 0 ? (
                            <tr><td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>No stores found. Try syncing.</td></tr>
                        ) : stores?.map(s => (
                            <tr key={s.legacyStoreId} style={{ borderBottom: '1px solid var(--color-border)' }}>
                                <td style={{ padding: '1rem', color: 'var(--color-text-tertiary)', fontFamily: 'monospace' }}>{s.legacyStoreId}</td>
                                <td style={{ padding: '1rem', fontWeight: 500 }}>{s.name}</td>
                                <td style={{ padding: '1rem' }}>{s.city} {s.province && `, ${s.province}`}</td>
                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={s.isActive}
                                            onChange={(e) => toggleStoreMutation.mutate({ id: s.legacyStoreId, isActive: e.target.checked })}
                                            disabled={toggleStoreMutation.isPending}
                                        />
                                        <span className="toggle-slider" />
                                    </label>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
